/**
 * Snapping COM PROVENIENCIA.
 *
 * O ponto que precisa ficar claro: o snap nao "move um pixel". Ele decide QUE TIPO
 * DE PONTO esta sendo criado.
 *
 *  - clicou perto do cruzamento de dois arcos  -> `{ via: 'intersection', a, b, branch }`
 *  - clicou no vazio                            -> `{ via: 'free', at }`
 *
 * Essa distincao e a base de toda a correcao do N4. Se o snap apenas ajustasse a
 * coordenada, um ponto colocado a olho e um ponto construido ficariam
 * indistinguiveis, a checagem de robustez perderia o sentido e o item passaria a
 * medir pontaria em vez de geometria.
 */

import type { Vec2 } from './vec.ts';
import { dist } from './vec.ts';
import type { GeoId, GeoObject, GCircle, GLine } from './construction.ts';
import { asCircleGeom, asLineGeom, intersectionPoints } from './construction.ts';
import { distPointRay, distPointSegment, distPointLine, paramOnLine } from './primitives.ts';
import { angleOf, sub } from './vec.ts';

export interface SnapConfig {
  /** Raio de captura em PIXELS de tela (convertido para unidades de modelo por `scale`). */
  readonly pixelRadius: number;
  readonly lattice: { readonly on: boolean; readonly step: number };
  readonly toPoints: boolean;
  readonly toIntersections: boolean;
  /** Pousar sobre uma reta/circulo gera um ponto `on-object`. */
  readonly toObjects: boolean;
}

export const DEFAULT_SNAP: SnapConfig = {
  pixelRadius: 12,
  lattice: { on: false, step: 1 },
  toPoints: true,
  toIntersections: true,
  toObjects: true,
};

export type SnapResult =
  | { readonly to: 'none'; readonly at: Vec2 }
  | { readonly to: 'point'; readonly id: GeoId; readonly at: Vec2 }
  | {
      readonly to: 'intersection';
      readonly a: GeoId;
      readonly b: GeoId;
      readonly branch: 0 | 1;
      readonly at: Vec2;
    }
  | { readonly to: 'object'; readonly host: GeoId; readonly t: number; readonly at: Vec2 }
  | { readonly to: 'lattice'; readonly i: number; readonly j: number; readonly at: Vec2 };

interface Candidate {
  readonly result: SnapResult;
  readonly d: number;
  /** Prioridade: menor vence, mesmo se um pouco mais longe. */
  readonly rank: number;
}

/**
 * `scale` = unidades de modelo por pixel de tela. O raio de captura e definido em
 * pixels de proposito: e a precisao do dedo/ponteiro que manda, nao a escala do
 * problema -- assim o zoom se comporta.
 */
export function snap(
  p: Vec2,
  objs: Iterable<GeoObject>,
  cfg: SnapConfig,
  scale: number,
): SnapResult {
  const radius = cfg.pixelRadius * scale;
  const all = [...objs];
  const cands: Candidate[] = [];

  // 1. pontos existentes (prioridade maxima)
  if (cfg.toPoints) {
    for (const o of all) {
      if (o.kind !== 'point') continue;
      const d = dist(p, o.pos);
      if (d <= radius) cands.push({ result: { to: 'point', id: o.id, at: o.pos }, d, rank: 0 });
    }
  }

  // 2. interseccoes entre pares de objetos nao-pontuais
  if (cfg.toIntersections) {
    const nonPoints = all.filter((o): o is GLine | GCircle => o.kind !== 'point');
    for (let i = 0; i < nonPoints.length; i++) {
      for (let j = i + 1; j < nonPoints.length; j++) {
        const a = nonPoints[i] as GLine | GCircle;
        const b = nonPoints[j] as GLine | GCircle;
        const pts = intersectionPoints(a, b);
        for (let k = 0; k < pts.length && k < 2; k++) {
          const q = pts[k] as Vec2;
          if (!onVisiblePart(a, q) || !onVisiblePart(b, q)) continue;
          const d = dist(p, q);
          if (d <= radius) {
            cands.push({
              result: { to: 'intersection', a: a.id, b: b.id, branch: k as 0 | 1, at: q },
              d,
              rank: 1,
            });
          }
        }
      }
    }
  }

  // 3. malha de inteiros
  if (cfg.lattice.on) {
    const step = cfg.lattice.step;
    const i = Math.round(p.x / step);
    const j = Math.round(p.y / step);
    const q = { x: i * step, y: j * step };
    const d = dist(p, q);
    if (d <= radius) cands.push({ result: { to: 'lattice', i, j, at: q }, d, rank: 2 });
  }

  // 4. sobre um objeto
  if (cfg.toObjects) {
    for (const o of all) {
      if (o.kind === 'point') continue;
      const hit = projectOnObject(o, p);
      if (!hit) continue;
      const d = dist(p, hit.at);
      if (d <= radius) {
        cands.push({ result: { to: 'object', host: o.id, t: hit.t, at: hit.at }, d, rank: 3 });
      }
    }
  }

  if (cands.length === 0) return { to: 'none', at: p };

  cands.sort((x, y) => (x.rank !== y.rank ? x.rank - y.rank : x.d - y.d));
  return (cands[0] as Candidate).result;
}

/** O ponto cai na parte VISIVEL do objeto? (um segmento nao se estende ao infinito) */
function onVisiblePart(o: GLine | GCircle, q: Vec2): boolean {
  if (o.kind === 'circle') return true;
  const tolAbs = 1e-7 * Math.max(1, dist(o.p, o.q));
  if (o.span === 'segment') return distPointSegment(q, o.p, o.q) <= tolAbs;
  if (o.span === 'ray') return distPointRay(q, o.p, o.q) <= tolAbs;
  return distPointLine(q, asLineGeom(o)) <= tolAbs;
}

function projectOnObject(o: GLine | GCircle, p: Vec2): { at: Vec2; t: number } | null {
  if (o.kind === 'circle') {
    const k = asCircleGeom(o);
    const d = dist(p, k.c);
    if (d < 1e-12) return null;
    const ang = angleOf(sub(p, k.c));
    return { at: { x: k.c.x + k.r * Math.cos(ang), y: k.c.y + k.r * Math.sin(ang) }, t: ang };
  }

  const L = Math.max(dist(o.p, o.q), 1e-9);
  let t = paramOnLine(p, asLineGeom(o)) / L;
  if (o.span === 'segment') t = Math.max(0, Math.min(1, t));
  else if (o.span === 'ray') t = Math.max(0, t);
  return { at: { x: o.p.x + o.dir.x * t * L, y: o.p.y + o.dir.y * t * L }, t };
}

/** Rotulo curto do snap, para a dica visual da interface. */
export function snapLabel(s: SnapResult): string {
  switch (s.to) {
    case 'point':
      return 'ponto';
    case 'intersection':
      return 'intersecao';
    case 'lattice':
      return 'malha';
    case 'object':
      return 'sobre o objeto';
    case 'none':
      return '';
  }
}
